import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

// components
import CheckoutForm from "core/CheckoutForm";
import SubscriptionLicenseBtns from "subscriptions/components/SubscriptionLicenseBtns";
import DisclaimerBodyText from "subscriptions/components/DisclaimerBodyText";
import {
    CheckoutProvider,
    usePromoCode,
    useSubscriptionPrices,
    useUpdateBillingInfo
} from "frontend/context/CheckoutContext";

// helpers
import { useHistory } from "react-router-dom";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { roundToHundredth } from "frontend/components/helpers";
import { processTransaction } from "subscriptions/helpers";
import { useCheckoutFormGeneralError } from "frontend/context/CheckoutContext";
import { PayPalNounProSubProvider } from "frontend/context/PayPalNounProSubContext";

// styles
import { FormContainer } from "subscriptions/styles";
import { GeneralFormErrorMessage } from "icons/IconCheckout/styles";

// data
import {
    NOUN_PRO_TEAM_CONFIRMATION_PAGE,
    NOUN_PRO_TEAM_LANDING_PAGE
} from "data/links";
import {
    subscribeTeamURL,
    TEAM_MONTHLY_PLAN,
    TEAM_YEARLY_PLAN
} from "subscriptions/data";

import {
    MONTHLY_PRICE_PLAN,
    ANNUAL_PRICE_AS_MONTHLY,
    ANNUAL_PRICE_AS_YEARLY
} from "core/constants";
import { useQuery } from "@apollo/react-hooks";
import { GET_SAVED_CARD } from "photos/PhotoCheckout/queries";

const UnwrappedForm = ({
    showOrderSummary,
    planType,
    setForm,
    form,
    numSeats,
    teamUser
}) => {
    const history = useHistory();
    const stripe = useStripe();
    const elements = useElements();
    const {
        generalFormError,
        setGeneralFormError
    } = useCheckoutFormGeneralError();

    const { promoCodeValue } = usePromoCode();
    const {
        submitBillingInfo,
        setReceiptInfo,
        setTeamUsername
    } = useUpdateBillingInfo();

    const {
        subscriptionPrices,
        setSubscriptionPrices
    } = useSubscriptionPrices();

    const [saveCard, setSaveCard] = useState(false);
    const [loading, setLoading] = useState(false);
    const [licenseSelection, setLicenseSelection] = useState(
        planType === TEAM_MONTHLY_PLAN ? TEAM_MONTHLY_PLAN : TEAM_YEARLY_PLAN
    );

    // query for saved card data:
    const { data: { savedCard: response = {} } = {} } = useQuery(
        GET_SAVED_CARD,
        {
            notifyOnNetworkStatusChange: true
        }
    );

    useEffect(() => {
        if (teamUser) setTeamUsername(teamUser);
    }, []);

    useEffect(() => {
        if (response && response.receiptInfo) {
            setReceiptInfo(response.receiptInfo);
        }
    }, [response]);

    useEffect(() => {
        setSubscriptionPrices({
            yearly: ANNUAL_PRICE_AS_YEARLY * parseInt(numSeats),
            monthly: MONTHLY_PRICE_PLAN * parseInt(numSeats)
        });
    }, []);

    const handleFormChange = (value, fieldName) => {
        setForm({ ...form, [fieldName]: value });
        setGeneralFormError("");
    };

    const submitFormWithNewCardInfo = async e => {
        e.preventDefault();

        if (!form.name) {
            return setGeneralFormError("Please fill out all required fields");
        }

        setLoading(true);

        const isSuccess = await submitBillingInfo();
        if (isSuccess) finalPurchase();
    };

    const finalPurchase = async () => {
        const body = {
            plan: licenseSelection,
            seats: numSeats,
            coupon: promoCodeValue
        };

        const routeURL = `/${teamUser}${subscribeTeamURL}`;

        const { formError, success } = await processTransaction(
            form,
            body,
            routeURL,
            stripe,
            elements
        );

        if (success) {
            setLoading(false);
            history.replace(NOUN_PRO_TEAM_CONFIRMATION_PAGE);
        } else if (formError) {
            setLoading(false);
            setGeneralFormError(formError);
        }
    };

    const saveCardCheckboxOnClick = () => {
        setSaveCard(!saveCard);
    };

    return (
        <>
            <SubscriptionLicenseBtns
                setLicenseSelection={setLicenseSelection}
                subscriptionPrices={subscriptionPrices}
                licenseSelection={licenseSelection}
                teamCheckout
            />
            <FormContainer>
                {generalFormError && (
                    <GeneralFormErrorMessage
                        handleDismissClick={() => setGeneralFormError("")}
                        withHeader
                    >
                        {generalFormError}
                    </GeneralFormErrorMessage>
                )}
                <CheckoutForm
                    cancelLink={NOUN_PRO_TEAM_LANDING_PAGE}
                    form={form}
                    handleFormChange={handleFormChange}
                    hasSavedCard={false}
                    hideCardLinks
                    isSubmitDisabled={false}
                    itemPrice={
                        licenseSelection === TEAM_MONTHLY_PLAN
                            ? subscriptionPrices.monthly
                            : subscriptionPrices.yearly
                    }
                    licenseSelection={licenseSelection}
                    loading={loading}
                    numSeats={numSeats}
                    promoCodeValue={promoCodeValue}
                    saveCardCheckboxOnClick={saveCardCheckboxOnClick}
                    saveCardInitialValue={false}
                    showOrderSummary={showOrderSummary}
                    showPayPalPayment={false}
                    showPromoCode={true}
                    submitFormWithNewCardInfo={submitFormWithNewCardInfo}
                    submitText="Start Subscription"
                />
            </FormContainer>

            <DisclaimerBodyText licenseSelection={licenseSelection} />
        </>
    );
};

const Form = props => (
    <CheckoutProvider>
        <PayPalNounProSubProvider>
            <UnwrappedForm {...props} />
        </PayPalNounProSubProvider>
    </CheckoutProvider>
);

Form.propTypes = {
    showOrderSummary: PropTypes.bool.isRequired,
    planType: PropTypes.string.isRequired,
    setForm: PropTypes.func.isRequired,
    form: PropTypes.shape({
        name: PropTypes.string,
        address_zip: PropTypes.string,
        receipt_info: PropTypes.string
    }),
    numSeats: PropTypes.string.isRequired,
    teamUser: PropTypes.string.isRequired
};

export default Form;
